import React, { useEffect, Suspense } from 'react';
import { createPortal } from 'react-dom';
import AppLoader from '../AppLoader/AppLoader';
import useMapAreaPortalTarget from '../../hooks/useMapAreaPortalTarget';
import './MapCombatPanel.css';

// Node-type → display metadata. Keeps the panel standalone so the
// migration of each session type (drill/trial/content/boss/fountain/resus)
// can plug in just by extending this table + the body renderer.
const NODE_TYPE_META = {
  drill: { trait: 'Objective Drill', color: '#3b82f6', icon: 'drill' },
  trial: { trait: 'Challenge Trial', color: '#a78bfa', icon: 'trial' },
  content: { trait: 'Teaching Card', color: '#22c55e', icon: 'content' },
  fountain: { trait: 'Dialysis Reward', color: '#06b6d4', icon: 'fountain' },
  boss: { trait: 'Boss Fight', color: '#ef4444', icon: 'boss' },
  resuscitate: { trait: 'Revive Gauntlet', color: '#f0eaff', icon: 'portal' },
  topic: { trait: 'Level Session', color: '#22c55e', icon: 'topic' },
};

const META_FALLBACK = { trait: 'Session', color: '#818cf8', icon: 'default' };

function renderIcon(iconKey) {
  const stroke = 'currentColor';
  switch (iconKey) {
    case 'drill':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 22 12 12 22 2 12" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      );
    case 'trial':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 15 9 22 9 17 14 19 22 12 18 5 22 7 14 2 9 9 9 12 2" />
        </svg>
      );
    case 'content':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h12a4 4 0 0 1 4 4v12" />
          <path d="M4 4v16h12" />
          <path d="M8 9h8" />
          <path d="M8 13h5" />
        </svg>
      );
    case 'fountain':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v6" />
          <path d="M8 7h8" />
          <path d="M4 21a8 8 0 0 1 16 0" />
          <path d="M12 9v4" />
          <path d="M9 13h6" />
        </svg>
      );
    case 'boss':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l4 5 5 1-4 4 1 6-6-3-6 3 1-6-4-4 5-1z" />
        </svg>
      );
    case 'portal':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <ellipse cx="12" cy="12" rx="7" ry="10" />
          <ellipse cx="12" cy="12" rx="4" ry="6" />
        </svg>
      );
    case 'topic':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3v9l6 3" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
}

/**
 * MapCombatPanel — inline map-viewport modal. Portal'd into `.deck-map-area`
 * so the glass backdrop and panel fill the map hero area (not the whole
 * page), matching the InfectionOrb/minigame dialog style.
 *
 * Body is a stub for v2 — the session interior (LevelSession, BossSession,
 * ResuscitatePage...) will mount here in the follow-up migration.
 */
export default function MapCombatPanel({ node, onClose, onBegin, children }) {
  useEffect(() => {
    if (!node) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [node, onClose]);

  // Stabilized portal host — see useMapAreaPortalTarget. Prevents the
  // synchronous re-parent flash when `.deck-map-area` blinks for a frame.
  const target = useMapAreaPortalTarget(!!node);

  if (!node) return null;

  const meta = NODE_TYPE_META[node.type] || META_FALLBACK;
  const creatureColor = node.color || meta.color;
  const label = node.label || node.short_label || meta.trait;
  const variant = node.type === 'resuscitate' ? 'resuscitate' : 'level';

  const panel = (
    <div
      className="mcp-overlay"
      style={{ '--creature-color': creatureColor }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${meta.trait}: ${label}`}
    >
      <div className="mcp-body" data-modal-variant={variant} onClick={(e) => e.stopPropagation()}>
        <button className="mcp-close" onClick={onClose} aria-label="Close session">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* Combat-styled header */}
        <div className="mcp-header">
          <div className="mcp-avatar">{renderIcon(meta.icon)}</div>
          <div className="mcp-name-col">
            <span className="mcp-name">{label}</span>
            <span className="mcp-trait">{meta.trait}</span>
          </div>
        </div>

        {/* Session content — children (when provided by SessionModalRoute)
            render the real session component wired to the actual URL. The
            stub path is kept for chrome iteration via the legacy dev hook. */}
        <div className="mcp-content">
          {children ? (
            <div className="mcp-embedded-session">
              <Suspense fallback={
                <div className="app-loader-shell app-loader-shell--panel">
                  <AppLoader label="Loading combat..." size="md" variant="boss" />
                </div>
              }>
                {children}
              </Suspense>
            </div>
          ) : (
            <div className="mcp-stub">
              <span className="mcp-stub-label">{meta.trait}</span>
              <h3 className="mcp-stub-title">{label}</h3>
              <p className="mcp-stub-hint">
                Session chrome prototype. Question flow will render here inline, no more route jumps.
              </p>
              <button className="mcp-primary-btn" onClick={() => onBegin && onBegin(node)}>
                Begin session
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Mount into the map viewport container so the overlay only covers the
  // hero area; the hook falls back to document.body if the map isn't present
  // (e.g. dashboard still booting) and upgrades once it mounts.
  if (!target) return null;
  return createPortal(panel, target);
}
